import { useCallback, useRef, type ReactElement, type ReactNode } from "react";

import { cn } from "@/lib/utils";

import { useHubToast } from "./HubToast";

const REPEAT_GUARD_MS = 2000;

/** Warning toast for a disabled control, ignoring a repeat tap of the same reason. */
export function useDisabledTapToast(): (reason: string) => void {
  const { showToast } = useHubToast();
  const lastRef = useRef<{ message: string; at: number } | null>(null);
  return useCallback(
    (reason: string) => {
      const message = reason.trim();
      if (message === "") return;
      const now = Date.now();
      const last = lastRef.current;
      if (last != null && last.message === message && now - last.at < REPEAT_GUARD_MS) return;
      lastRef.current = { message, at: now };
      showToast({ message, tone: "warning" });
    },
    [showToast],
  );
}

/**
 * Keeps a disabled child looking disabled, and shows `reason` when the operator taps it.
 * The child must stay `disabled` so it does not run its action. Pointer events pass through
 * to this wrapper.
 */
export function TapDisabledReason({
  reason,
  fill = false,
  className,
  children,
}: {
  reason: string | null;
  /** Stretch to the parent, for full-width tiles. */
  fill?: boolean;
  className?: string;
  children: ReactNode;
}): ReactElement {
  const explain = useDisabledTapToast();
  const blocked = reason != null && reason.trim() !== "";
  return (
    <span
      className={cn(
        "touch-manipulation",
        fill ? "flex h-full w-full" : "inline-flex max-w-full",
        blocked && "cursor-not-allowed",
        className,
      )}
      onClick={() => {
        if (!blocked || reason == null) return;
        explain(reason);
      }}
    >
      <span className={cn(fill ? "flex h-full w-full" : "inline-flex", blocked && "pointer-events-none")}>
        {children}
      </span>
    </span>
  );
}
